import {
  Box,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  LICENSE_FORMAT_INFO,
  validateLicenseFormat,
} from 'views/quote_marketplace/constants/policyOptions';
import type { MVRDriverRow as MVRDriverRowData } from '../hooks/useDriverList';

interface MvrDriverRowProps {
  driver: MVRDriverRowData;
  index: number;
  province: number | null;
  canRemove: boolean;
  onChange: (
    field: keyof Omit<MVRDriverRowData, 'id'>,
    value: string | number,
  ) => void;
  onRemove: () => void;
}

const MvrDriverRow = ({
  driver,
  index,
  province,
  canRemove,
  onChange,
  onRemove,
}: MvrDriverRowProps) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const licenseValidation =
    province !== null
      ? validateLicenseFormat(driver.licenceNumber, province)
      : { valid: true };
  const licensePlaceholder =
    province !== null
      ? LICENSE_FORMAT_INFO[province]?.example || 'License #'
      : 'License #';

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="xs" fontWeight="700" color="gray.500">
          Driver {index + 1}
        </Text>
        <Tooltip label="Remove driver">
          <IconButton
            aria-label="Remove driver"
            icon={<CloseIcon />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={onRemove}
            isDisabled={!canRemove}
          />
        </Tooltip>
      </Flex>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
        <GridItem>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              variant="main"
              value={driver.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="Full name"
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl isRequired isInvalid={!licenseValidation.valid}>
            <FormLabel fontSize="sm">Licence No.</FormLabel>
            <Input
              variant="main"
              value={driver.licenceNumber}
              onChange={(e) => onChange('licenceNumber', e.target.value)}
              placeholder={licensePlaceholder}
            />
            {!licenseValidation.valid && (
              <FormErrorMessage>{licenseValidation.error}</FormErrorMessage>
            )}
          </FormControl>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default MvrDriverRow;
